import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  Avatar,
  Stack,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { CompanyLayout } from '../../components/Layout';
import { useSponsorSettings } from '../../hooks/useSponsorSettings';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyId } from '../../hooks/useCompanyId';

const CompanySponsorSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const {
    companyId,
    loading: companyLoading,
    error: companyError,
    isCompanyUser,
  } = useCompanyId();

  const { settings, loading, error } = useSponsorSettings(companyId);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const formatUpdatedAt = (isoString?: string) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const sponsorDescription = settings?.isSponsor
    ? '学生のタイムライン上部にロゴが掲載されます。リード獲得の認知向上にご活用ください。'
    : 'スポンサー未設定です。スポンサー設定を有効化すると学生向けタイムラインで企業ロゴが表示されます。';

  // ローディング中
  if (companyLoading) {
    return (
      <CompanyLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </CompanyLayout>
    );
  }

  // 企業ユーザーでない、または企業IDが取得できない場合
  if (!isCompanyUser || !companyId) {
    return (
      <CompanyLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              アクセス権限エラー
            </Typography>
            <Typography variant="body2">
              {!isCompanyUser
                ? 'このページは企業アカウント専用です。企業管理者、HR、または従業員としてログインしてください。'
                : companyError ||
                  '企業情報の取得に失敗しました。管理者にお問い合わせください。'}
            </Typography>
            {user && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                現在のユーザー権限: {user.userRole}
              </Typography>
            )}
          </Alert>
        </Box>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            スポンサー設定状況
          </Typography>
          <Typography variant="body1" color="text.secondary">
            現在のスポンサー設定状況を確認できます。設定変更は管理者が行います。
          </Typography>
        </Box>

        {/* 管理者による設定の案内 */}
        <Alert severity="info" sx={{ mb: 3 }} icon={<AdminIcon />}>
          <Typography variant="body2">
            <strong>スポンサー設定について</strong>
            <br />
            スポンサー設定の変更は管理者が行います。設定変更をご希望の場合は、管理者にお問い合わせください。
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {settings?.isSponsor && !settings.logoUrl && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            スポンサー表示中ですが、ロゴURLが設定されていません。管理者にロゴの登録をご依頼ください。
          </Alert>
        )}

        {loading && !settings ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 700, mx: 'auto' }}>
            {/* 現在の設定表示 */}
            <Card>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 3, md: 4 }}
                >
                  <Stack spacing={2} flex={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Chip
                        icon={
                          settings?.isSponsor ? (
                            <StarIcon />
                          ) : (
                            <StarBorderIcon />
                          )
                        }
                        label={
                          settings?.isSponsor ? 'スポンサー企業' : '一般企業'
                        }
                        color={settings?.isSponsor ? 'primary' : 'default'}
                        sx={{ fontSize: '0.9rem', fontWeight: 600 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        最終更新: {formatUpdatedAt(settings?.updatedAt)}
                      </Typography>
                    </Stack>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: settings?.isSponsor ? 'primary.50' : 'grey.50',
                        borderColor: settings?.isSponsor
                          ? 'primary.light'
                          : 'divider',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        スポンサー表示設定
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        {sponsorDescription}
                      </Typography>
                    </Paper>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        管理者への依頼事項
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        設定変更をご希望の場合は、管理者にご連絡ください。ロゴの差し替えは企業プロフィール管理画面から更新できます。
                      </Typography>
                    </Stack>
                  </Stack>

                  {isMdUp && <Divider orientation="vertical" flexItem />}

                  {!isMdUp && (
                    <Divider orientation="horizontal" flexItem sx={{ my: 2 }} />
                  )}

                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                    >
                      ロゴプレビュー
                    </Typography>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}
                    >
                      <Avatar
                        src={settings?.logoUrl || undefined}
                        alt="企業ロゴ"
                        sx={{
                          width: 96,
                          height: 96,
                          bgcolor: 'common.white',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <BusinessIcon
                          sx={{ fontSize: 40, color: 'grey.500' }}
                        />
                      </Avatar>
                    </Box>
                    {settings?.logoUrl ? (
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: 'break-all',
                          bgcolor: 'background.paper',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px dashed',
                          borderColor: 'divider',
                          textAlign: 'left',
                        }}
                      >
                        {settings.logoUrl}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        ロゴURLが設定されていません
                      </Typography>
                    )}
                  </Paper>
                </Stack>
              </CardContent>
            </Card>

            {/* 説明 */}
            <Alert severity="info" sx={{ mt: 3, fontSize: '0.875rem' }}>
              スポンサー企業として設定すると、学生のタイムライン上部にカルーセル形式で企業ロゴが表示されます。
            </Alert>
          </Box>
        )}
      </Box>
    </CompanyLayout>
  );
};

export default CompanySponsorSettingsPage;
